<!--
  Created 16.06.2023
  Modal for adding new entities.
  Uses SimpleModal component as a base.
-->
<template>
  <SimpleModal
    :show="showCreateEntityModal"
    :box-class="[
      'w-11/12',
      'md:w-4/5',
      step>=2 ? 'lg:w-10/12 h-4/5' : 'lg:w-7/12',
    ]"
    :outside-click="false"
    @update:show="(value) => showCreateEntityModal = value">
    <template #content>
      <div class="flex size-full flex-col">
        <header class="w-full border-b border-gray-200 bg-gray-100 p-4">
          <h1 class="text-xl font-medium text-gray-700">
            New entity
          </h1>
          <div class="mt-1 flex justify-between">
            <p
              v-if="step === 1"
              class="text-sm text-gray-600">
              Get started by selecting in which category you want to add the new entity.
            </p>
            <p
              v-else
              class="text-sm text-gray-600">
              Type in the name {{ picked ? 'of the ' + entityTypes?.find((e) => { return e.id === picked}).id : '' }},
              {{ picked === 'global' ? 'and add it with the button below' : 'and we will start searching for it in Google Maps' }}.
            </p>
            <p class="ml-4 min-w-fit text-sm text-gray-600">
              Step {{ step }} of 2
            </p>
          </div>
        </header>
        <main class="flex-1 text-gray-700">
          <!--
        Step 1
        -->
          <div
            v-show="step === 1"
            class="flex flex-row p-4">
            <div class="basis-1/3">
              <h2 class="text-sm font-medium">
                What type of entity?
              </h2>
            </div>
            <div class="basis-2/3">
              <fieldset>
                <legend class="sr-only">
                  Plan
                </legend>
                <div class="space-y-5">
                  <div
                    v-for="entityType in entityTypes"
                    :key="entityType.id"
                    class="relative flex items-start">
                    <div class="flex h-6 items-center">
                      <input
                        :id="entityType.id"
                        v-model="picked"
                        :value="entityType.id"
                        :aria-describedby="`${entityType.id}-description`"
                        name="entityType"
                        type="radio"
                        :checked="entityType.id === picked"
                        class="size-4 border-gray-300 text-indigo-600 hover:cursor-pointer focus:ring-indigo-600">
                    </div>
                    <div class="ml-3 text-sm leading-5">
                      <label
                        :for="entityType.id"
                        class="hover:cursor-pointer">
                        <p class="font-medium text-gray-700">{{ entityType.name }}</p>
                        <p
                          :id="`${entityType.id}-description`"
                          class="text-gray-500">
                          {{ entityType.description }}
                        </p>
                      </label>
                    </div>
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
          <!--
        Step 2
        -->
          <div
            v-show="step === 2"
            class="flex h-full flex-row">
            <!-- Search -->
            <div class="flex basis-1/2 flex-col">
              <!-- Search input -->
              <div class="flex flex-row items-center justify-between p-4">
                <h2 class="basis-2/5 text-sm font-medium">
                  {{ picked ? entityTypes.find((e) => { return e.id === picked }).name : 'Entity' }}
                </h2>
                <input
                  v-model="search"
                  type="search"
                  :placeholder="picked === 'global' ? 'Type in entity name' : 'Search for entity by name'"
                  class="basis-3/5 rounded-md border border-gray-300">
              </div>
              <!-- Search results -->
              <div class="flex h-full flex-col bg-gray-100 ">
                <div>
                  <p class="p-4 text-xs text-gray-500">
                    {{ picked === 'global'
                      ? (selected && selected.place_id === null
                        ? 'Type in your global entity\'s name, and add as custom by clicking the button below.'
                        : 'Global entities are custom and can be used across all destinations, and therefore can\'t be imported from Google nor have a location.' +
                          ' Please type in what you would like to name your global entity in the input box above.')
                      : (selected && selected.place_id === null
                        ? 'Adding custom entity. You can switch back to Google results by clicking the button below.'
                        : 'Search results from Google maps. Select one to import to your company\'s database.')
                    }}
                  </p>
                </div>
                <div class="google-results flex h-full flex-col p-4 pt-0">
                  <!-- spinner icon -->
                  <Spinner
                    v-if="loading"
                    class="size-20 mx-auto" />
                  <div v-else-if="selected && selected.place_id === null">
                    <button
                      v-if="search"
                      :key="'new'"
                      type="button"
                      class="group cursor-pointer border
                      border-gray-200 bg-white hover:bg-cyan-50"
                      :class="{
                        'selected' : true
                      }">
                      <div class="flex flex-row justify-between border-l-2 p-1 pl-2 group-[&.selected]:border-cyan-600">
                        <div class="flex flex-col items-start pr-20">
                          <p class="font-semibold group-[&.selected]:text-cyan-600">
                            {{ search }}
                          </p>
                          <p class="text-xs text-gray-500">
                            Adding custom {{ picked }}. You can edit information after it's been added.
                          </p>
                          <p
                            v-if="picked !== 'global'"
                            class="text-xs">
                            Move the marker to the exact location for this {{ picked }} before adding this entity.
                          </p>
                        </div>
                      </div>
                    </button>
                  </div>
                  <!-- google results -->
                  <div
                    v-else
                    class="flex flex-1 basis-0 flex-col overflow-y-scroll">
                    <button
                      v-for="entity in entities"
                      :key="entity.place_id"
                      :ref="(selected !== null && entity.place_id === selected.place_id ? 'active' : '')"
                      type="button"
                      class="group cursor-pointer border-b border-r border-gray-200
                      bg-white hover:bg-cyan-50 [&:first-of-type]:border-t"
                      :class="{
                        'exists': entity.exists,
                        'selected' : selected !== null && entity.place_id === selected.place_id
                      }"
                      @click="select(entity)">
                      <div class="flex flex-row justify-between border-l-2 p-1 pl-2 group-[&.selected]:border-cyan-600">
                        <div class="flex flex-col items-start text-left">
                          <p class="font-semibold group-[&.selected]:text-cyan-600">
                            {{ entity.name }} <small
                              v-if="entity.exists"
                              class="text-green-800">(already imported to {{ picked[0].toUpperCase() + picked.slice(1) }})</small>
                          </p>
                          <p class="text-xs capitalize text-gray-500">
                            {{ entity.type.split(",")[0].replace(/_/gi, ' ') }}, {{ entity.rating }}/5 ({{ entity.user_ratings_total }} reviews)
                          </p>
                          <p
                            v-if="entity.description"
                            class="text-xs">
                            {{ stringLimit(entity.description, 90) }}
                          </p>
                        </div>
                        <span class="relative mr-3 flex items-center">
                          <Tooltip
                            class="mr-1"
                            align="right">
                            <template #trigger>
                              <ExclamationCircleIcon
                                v-if="entity?.within50km === false"
                                class="size-6 text-red-700"
                                title="This item is more than 50 km away from city center."
                                aria-hidden="true"
                                aria-label="This item is more than 50 km away from city center." />
                            </template>
                            <template #content>
                              <p class="text-xs">
                                This item is more than 50 km away from city center.
                              </p>
                            </template>
                          </Tooltip>
                          <svg
                            v-if="selected !== null && entity.place_id === selected.place_id"
                            width="14"
                            height="8"
                            viewBox="0 0 14 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M10.4802 1.40271L13.1815 4.10408M13.1815 4.10408L10.4802 6.80544M13.1815 4.10408L1.02539 4.10407"
                              stroke="#111827"
                              stroke-width="1.35068"
                              stroke-linecap="round"
                              stroke-linejoin="round" />
                          </svg>
                          <svg
                            v-else
                            width="14"
                            class="hidden group-hover:block"
                            height="8"
                            viewBox="0 0 14 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M10.4802 1.40271L13.1815 4.10408M13.1815 4.10408L10.4802 6.80544M13.1815 4.10408L1.02539 4.10407"
                              stroke="#111827"
                              stroke-width="1.35068"
                              stroke-linecap="round"
                              stroke-linejoin="round" />
                          </svg>
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <!-- Map with results -->
            <div class="basis-1/2">
              <!-- Map of destination -->
              <GoogleMaps
                :location="destination.location"
                :markers="markers"
                :new-marker="newMarker"
                filter-selected
                @update:newMarker="(value) => { newMarker ? selected.position = value.position : false }"
                @update:selected-marker="(value) => { newMarker ? false : select(entities.find((m) => m.place_id === value.id)) }" />
            </div>
          </div>
        </main>
        <footer
          class="flex border-t border-gray-200 bg-gray-100 p-4"
          :class="[(step === 2 && search && !loading && !timeOut) || (step === 2 && picked === 'global') ? 'justify-between' : 'justify-end']">
          <div v-if="step === 2">
            <button
              v-if="search && !loading && !timeOut"
              type="button"
              class="inline-flex items-center rounded-md border border-cyan-300 bg-cyan-100 px-4 py-2 text-sm
                font-medium text-gray-800 shadow-sm hover:bg-cyan-300
                focus:outline-none focus:ring-2 focus:ring-cyan-800 focus:ring-offset-2"
              :class="{'!focus:ring-gray-800 !border-gray-300 !bg-gray-100 !text-gray-600': selected}"
              @click="select({
                place_id: null, title:search, name:search, type: picked, parent_id: destination.id, position: {
                  lat: destination.location.coordinates[1], lng: destination.location.coordinates[0]
                }
              }, true)">
              <span v-if="selected && selected.place_id === null && picked === 'global'">Switch back</span>
              <span v-else-if="selected && selected.place_id === null">Switch back to Google results</span>
              <span v-else>Continue with <strong>{{ search }}</strong> as {{ picked === 'global' ? 'global' : 'custom' }} entity</span>
            </button>
            <div
              v-if="selected && selected.place_id === null"
              class="inline"
              :class="{'opacity-50': picked === 'global'}">
              <Checkbox
                id="location_disabled"
                v-model="locationDisabled"
                :checked="locationDisabled || picked === 'global'"
                :disabled="picked === 'global'"
                class="ml-4" />
              <label
                for="location_disabled"
                class="ml-1 text-sm text-gray-600">
                Disable location for this entity
              </label>
            </div>
            <div
              v-if="picked === 'global' && selected && selected.place_id === null"
              class="inline"
              :class="{'opacity-50': picked === 'global'}">
              <Checkbox
                id="entity_global"
                v-model="entityGlobal"
                :disabled="!locationDisabled || picked === 'global'"
                class="ml-4"
                :checked="entityGlobal && (locationDisabled || picked === 'global')" />
              <label
                for="entity_global"
                class="ml-1 text-sm text-gray-600">
                Make entity global
              </label>
            </div>
          </div>
          <div class="flex items-center gap-4">
            <button
              type="button"
              class="inline-flex items-center rounded-md border border-gray-300 bg-transparent px-4 py-2 text-sm
                font-medium text-gray-600 shadow-sm hover:bg-gray-300
                focus:outline-none focus:ring-2 focus:ring-purple-800 focus:ring-offset-2"
              @click="step === 1 ? showCreateEntityModal=false : --step">
              {{ (step === 1) ? 'Cancel' : 'Back' }}
            </button>
            <button
              type="button"
              class="inline-flex items-center rounded-md border border-transparent bg-purple-800 px-6 py-2 text-sm
                font-medium text-white shadow-sm hover:bg-purple-700
                focus:outline-none focus:ring-2 focus:ring-purple-800 focus:ring-offset-2"
              :class="(step === 1 && !picked) || (step === 2 && (!selected || selected.exists)) ? 'opacity-50 cursor-not-allowed' : 'opacity-100'"
              :disabled="(step === 1 && !picked) || (step === 2 && (!selected || selected.exists))"
              @click="step === 1 ? ++step : addEntity(selected)">
              {{ (step === 1 ) ? 'Next' : 'Add selected' }}
            </button>
          </div>
        </footer>
      </div>
    </template>
  </SimpleModal>
</template>

<script setup>
import {
  inject, onMounted, ref, watch,
} from 'vue';
import { ExclamationCircleIcon } from '@heroicons/vue/24/outline';
import SimpleModal from '@/Components/Modals/SimpleModal.vue';
import GoogleMaps from '@/Pages/Destination/Components/GoogleMaps.vue';
import Checkbox from '@/Jetstream/Checkbox.vue';
import Tooltip from '@/Components/Tooltip.vue';
import Spinner from '@/Components/Spinner.vue';

const emit = defineEmits(['add:entity']);

const props = defineProps({
  boxClass: {
    type: String,
    required: false,
    default: 'w-3/5 h-3/5',
  },
  destination: {
    type: Object,
    required: true,
  },
  entitiesGrouped: {
    type: Array,
    required: false,
    default: () => ([]),
  },
});

const entityTypes = [
  {
    id: 'lodging',
    name: 'Lodging',
    description: 'Places to stay overnight, like hotels, apartments etc.',
  },
  {
    id: 'eatery',
    name: 'Eatery',
    description: 'Places to eat, like restaurants, bars, cafes, street food etc.',
  },
  {
    id: 'activity',
    name: 'Activity',
    description: 'Things to do, like museums, biking, escape rooms, water-skiing etc.',
  },
  {
    id: 'venue',
    name: 'Venue',
    description: 'Where events takes place, such as concerts, conferences or weddings.',
  },
  {
    id: 'landmark',
    name: 'Landmark or Point of Interest',
    description: 'Points of interest, like airports, monuments, statues, parks etc.',
  },
  {
    id: 'other',
    name: 'Other',
    description: 'Other types of entities, that does not fit the other categories.',
  },
  {
    id: 'global',
    name: 'Global',
    description: 'Entities that exists across every destination (e.g. airport transportation).',
  },
];

const showCreateEntityModal = inject('showCreateEntityModal');

const step = ref(1);
const picked = ref('');

const entities = ref([]);
const entitiesPrev = ref([]);
const search = ref('');
const timeOut = ref(null);
const loading = ref(false);
const selected = ref(null);
const active = ref(null);
let timer;

/**
 *
 * @param deg
 */
function deg2rad(deg) {
  return deg * (Math.PI / 180);
}

/**
 *
 * @param lat1
 * @param lon1
 * @param lat2
 * @param lon2
 */
function getDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
    + Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2))
    * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in km

  return distance;
}

const markers = ref([]);

/**
 *
 */
function emptyForm() {
  entities.value = [];
  markers.value = [];
  search.value = '';
  selected.value = null;
}

/**
 *
 * @param empty
 */
function setMarkers(empty = false) {
  const entityMarkers = [];

  if (selected.value && !selected.value.within50km) {
    entities.value.forEach((entity) => {
      if (entity.place_id === selected.value.place_id) {
        entityMarkers.push(
          {
            id: entity.place_id,
            title: entity.name,
            position: {
              lat: entity.location.coordinates[1],
              lng: entity.location.coordinates[0],
            },
            within50km: entity.location?.coordinates[1]
              ? (getDistance(props.destination.location?.coordinates[1], props.destination.location?.coordinates[0], entity.location?.coordinates[1], entity.location?.coordinates[0]) <= 50)
              : null,
            selected: entity.place_id === selected.value?.place_id,
          },
        );
      }
    });
  } else {
    entities.value.forEach((entity) => {
      entityMarkers.push(
        {
          id: entity.place_id,
          title: entity.name,
          position: {
            lat: entity.location.coordinates[1],
            lng: entity.location.coordinates[0],
          },
          within50km: entity.location?.coordinates[1]
            ? (getDistance(props.destination.location?.coordinates[1], props.destination.location?.coordinates[0], entity.location?.coordinates[1], entity.location?.coordinates[0]) <= 50)
            : null,
          selected: entity.place_id === selected.value?.place_id,
        },
      );
    });
  }

  markers.value = entityMarkers;
}

const newMarker = ref(null);
const locationDisabled = ref(false);
const entityGlobal = ref(false);

/**
 *
 * @param value
 * @param size
 */
function stringLimit(value, size) {
  if (!value) {
    return '';
  }
  const text = value.toString();

  if (text.length <= size) {
    return text;
  }

  return `${text.substring(0, size)}...`;
}

const updateSearch = (skipTimer = false) => {
  loading.value = true;
  selected.value = null;
  entities.value = [];
  entitiesPrev.value = [];
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  timer = setTimeout(() => {
    axios.get(`${route('api.entity')}?destination=${props.destination.id}&type=${picked.value}&search=${search.value.length > 0 ? search.value : picked.value}`)
      .then((response) => {
        response.data.forEach((entity) => {
          entities.value.push({
            ...entity,
            exists: !!props.entitiesGrouped.map((e) => e.list).flat().find((el) => el.place_id === entity.id && el.type === picked.value),
            within50km: entity.location?.coordinates[1]
              ? (getDistance(props.destination.location?.coordinates[1], props.destination.location?.coordinates[0], entity.location?.coordinates[1], entity.location?.coordinates[0]) <= 50)
              : null,
          });
        });
        entitiesPrev.value = entities.value;
      }).finally(() => {
        setMarkers();
        loading.value = false;
        timer = null;
        timeOut.value = null;
      });
  }, skipTimer ? 0 : 500);
};

/**
 *
 * @param entity
 * @param custom
 */
function select(entity, custom = false) {
  if (selected?.value?.place_id === entity.place_id && picked.value !== 'global') {
    selected.value = null;
    locationDisabled.value = false;
    entityGlobal.value = false;
    if (custom) {
      entities.value = entitiesPrev.value;
    }
    setMarkers();

    return;
  }

  selected.value = entity;

  // scroll selected element into center of list
  setTimeout(() => {
    if (active.value !== null) {
      active.value[0]?.scrollIntoView({
        behavior: 'smooth', block: 'center',
      });
    }
  }, 100);

  if (custom) {
    entities.value = [];
    if (picked.value === 'global') {
      locationDisabled.value = true;
      entityGlobal.value = true;
      newMarker.value = null;
    } else {
      newMarker.value = entity;
    }
  }
  setMarkers();
}

/**
 *
 * @param entity
 */
function addEntity(entity) {
  if (!picked.value || !entity || entity.exists) {
    return;
  }
  // prepare text data
  const entityType = entity.type?.split(',')[0].replace(/_/gi, ' ') || '';
  const entityTypeCapitalised = (entityType && entityType[0].toUpperCase() + entityType.slice(1)) || '';
  const textObject = {
    name: entity.name,
    description: '',
    type: entityTypeCapitalised,
  };
  const postObject = {
    global: entityGlobal.value ?? false,
    type: picked.value,
    place_id: entity.place_id,
    title: 'Default',
    position: entity.position,
    // eslint-disable-next-line no-nested-ternary
    within50km: entity.position?.lat
      ? (getDistance(props.destination.location?.coordinates[1], props.destination.location?.coordinates[0], entity.position?.lat, entity.position?.lng) <= 50)
      : (entity.location?.coordinates[1]
        ? (getDistance(props.destination.location?.coordinates[1], props.destination.location?.coordinates[0], entity.location?.coordinates[1], entity.location?.coordinates[0]) <= 50)
        : null),
    location_disabled: locationDisabled.value ?? null,
    standard: true,
    data: {
      image: '/img/image_placeholder.jpg',
      type: entityTypeCapitalised,
      rating: entity.rating ?? null,
      user_ratings_total: entity.user_ratings_total ?? null,
    },
    texts: {
      no: textObject,
      en: textObject,
      sv: textObject,
      da: textObject,
    },
  };
  axios.post(route('api.place.entity.store', { parentPlace: props.destination.id }), postObject).then((response) => {
    emit('add:entity', {
      input: {
        ...entity,
        ...postObject,
        position_disabled: postObject.location_disabled ? 1 : null,
      },
      output: {
        ...response.data,
        position: entity.position,
        within50km: postObject.within50km,
        global: postObject.global,
      },
    });
  }).catch((error) => {
    console.error(error);
  }).finally(() => {
    showCreateEntityModal.value = false;
    setTimeout(() => {
      step.value = 1;
    }, 500);
  });
}

watch(() => step.value, () => {
  if (step.value === 1) {
    emptyForm();
    locationDisabled.value = false;
    entityGlobal.value = false;
  }
  if (step.value === 2 && picked.value !== 'global') {
    updateSearch(true);
  }
  if (step.value === 2 && picked.value === 'global') {
    locationDisabled.value = true;
    entityGlobal.value = true;
    newMarker.value = null;
  }
});

watch(() => search.value, () => {
  if (step.value === 2 && picked.value) {
    if (picked.value === 'global') {
      clearTimeout(timeOut.value);
      timeOut.value = setTimeout(() => {
        select({
          place_id: null,
          title: search.value,
          name: search.value,
          type: picked.value,
          parent_id: props.destination.id,
          position: {
            lat: props.destination.location.coordinates[1],
            lng: props.destination.location.coordinates[0],
          },
        }, true);
      }, 500);
    } else {
      clearTimeout(timeOut.value);
      timeOut.value = setTimeout(() => {
        updateSearch();
      }, 1000);
    }
  }
});

watch(() => locationDisabled.value, () => {
  if (locationDisabled.value) {
    newMarker.value = null;
  } else {
    newMarker.value = selected.value;
    entityGlobal.value = false;
  }
});

onMounted(() => {
  if (step.value === 2 && props.show && picked.value !== 'global') {
    updateSearch(true);
  }
});

</script>
